import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Info, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { paraBooleano } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import type { Especialidade } from '@/features/admin/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { BarraBusca } from '@/components/barra-busca';
import { Paginacao } from '@/components/paginacao';
import { Botao } from '@/components/ui/button';
import { AreaTexto, Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const esquema = z.object({
  nome: z.string().min(3, 'Informe o nome da especialidade.'),
  codigo: z.string().optional(),
  descricao: z.string().optional(),
  ativa: z.boolean().optional(),
});

type Formulario = z.infer<typeof esquema>;

const COLUNAS = 5;

export function PaginaEspecialidades() {
  const queryClient = useQueryClient();
  const [pagina, setPagina] = React.useState(1);
  const [busca, setBusca] = React.useState('');
  const [emEdicao, setEmEdicao] = React.useState<Especialidade | null>(null);
  const [dialogoAberto, setDialogoAberto] = React.useState(false);

  const parametros = new URLSearchParams({ pagina: String(pagina), limite: '20' });
  if (busca) parametros.set('busca', busca);

  const { data, isPending } = useQuery({
    queryKey: ['especialidades', pagina, busca],
    queryFn: () =>
      api.get<RespostaPaginada<Especialidade>>(`/especialidades?${parametros.toString()}`),
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { nome: '', codigo: '', descricao: '' },
  });

  const abrirCriacao = () => {
    setEmEdicao(null);
    formulario.reset({ nome: '', codigo: '', descricao: '' });
    setDialogoAberto(true);
  };

  const abrirEdicao = (especialidade: Especialidade) => {
    setEmEdicao(especialidade);
    formulario.reset({
      nome: especialidade.nome,
      codigo: especialidade.codigo ?? '',
      descricao: especialidade.descricao ?? '',
      ativa: especialidade.ativa,
    });
    setDialogoAberto(true);
  };

  const salvar = useMutation({
    mutationFn: (dados: Formulario) => {
      const corpo = {
        ...dados,
        codigo: dados.codigo || undefined,
        descricao: dados.descricao || undefined,
      };

      return emEdicao
        ? api.patch<Especialidade>(`/especialidades/${emEdicao.id}`, corpo)
        : api.post<Especialidade>('/especialidades', { ...corpo, ativa: undefined });
    },
    onSuccess: async () => {
      toast.success(emEdicao ? 'Especialidade atualizada.' : 'Especialidade cadastrada.');
      setDialogoAberto(false);
      await queryClient.invalidateQueries({ queryKey: ['especialidades'] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.');
    },
  });

  const aoEnviar = formulario.handleSubmit((dados) => salvar.mutate(dados));

  return (
    <>
      <CabecalhoPagina
        titulo="Especialidades"
        descricao="Catálogo usado pela UBS ao registrar uma solicitação."
        acoes={
          <Botao onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Nova especialidade
          </Botao>
        }
      />

      <div className="border-status-enviado-borda bg-status-enviado-fundo text-status-enviado-texto mb-4 flex items-start gap-2 rounded-md border p-3 text-sm">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          A lista atual é provisória, herdada do cadastro inicial. Substitua-a pela relação oficial
          da instituição antes de colocar o sistema em produção.
        </span>
      </div>

      <div className="mb-3">
        <BarraBusca
          valor={busca}
          aoMudar={(valor) => {
            setBusca(valor);
            setPagina(1);
          }}
          placeholder="Buscar por nome ou código"
        />
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Especialidade</TabelaTitulo>
            <TabelaTitulo>Código</TabelaTitulo>
            <TabelaTitulo>Solicitações</TabelaTitulo>
            <TabelaTitulo>Situação</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>Nenhuma especialidade encontrada.</TabelaVazia>
          )}

          {data?.dados.map((especialidade) => (
            <TabelaLinha key={especialidade.id}>
              <TabelaCelula rotulo="Especialidade">
                <div className="font-medium">{especialidade.nome}</div>
                {especialidade.descricao && (
                  <div className="text-silenciado-texto text-xs">{especialidade.descricao}</div>
                )}
              </TabelaCelula>
              <TabelaCelula rotulo="Código" className="text-silenciado-texto tabular-nums">
                {especialidade.codigo ?? '—'}
              </TabelaCelula>
              <TabelaCelula rotulo="Solicitações" className="tabular-nums">
                {especialidade._count?.solicitacoes ?? 0}
              </TabelaCelula>
              <TabelaCelula rotulo="Situação">
                <Etiqueta tom={especialidade.ativa ? 'sucesso' : 'neutro'}>
                  {especialidade.ativa ? 'Ativa' : 'Inativa'}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula className="text-right">
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => abrirEdicao(especialidade)}
                  aria-label={`Editar ${especialidade.nome}`}
                >
                  <Pencil aria-hidden="true" />
                  Editar
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={setPagina} />}

      <Dialogo open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>
              {emEdicao ? 'Editar especialidade' : 'Nova especialidade'}
            </DialogoTitulo>
            <DialogoDescricao>
              Especialidades não são excluídas, apenas inativadas — solicitações antigas precisam
              continuar legíveis.
            </DialogoDescricao>
          </DialogoCabecalho>

          <form onSubmit={(evento) => void aoEnviar(evento)} noValidate>
            <DialogoCorpo>
              <Campo
                id="esp-nome"
                rotulo="Nome"
                obrigatorio
                erro={formulario.formState.errors.nome?.message}
              >
                <Entrada autoFocus {...formulario.register('nome')} />
              </Campo>

              <Campo
                id="esp-codigo"
                rotulo="Código"
                ajuda="Código na tabela oficial (SIGTAP ou equivalente), se houver."
              >
                <Entrada {...formulario.register('codigo')} />
              </Campo>

              <Campo id="esp-descricao" rotulo="Descrição">
                <AreaTexto rows={3} {...formulario.register('descricao')} />
              </Campo>

              {emEdicao && (
                <Campo
                  id="esp-ativa"
                  rotulo="Situação"
                  ajuda="Inativar remove a especialidade de novas solicitações, sem afetar as existentes."
                >
                  <Selecao
                    {...formulario.register('ativa', {
                      setValueAs: paraBooleano,
                    })}
                  >
                    <option value="true">Ativa</option>
                    <option value="false">Inativa</option>
                  </Selecao>
                </Campo>
              )}
            </DialogoCorpo>

            <DialogoRodape>
              <Botao variante="contorno" onClick={() => setDialogoAberto(false)}>
                Cancelar
              </Botao>
              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                Salvar
              </Botao>
            </DialogoRodape>
          </form>
        </DialogoConteudo>
      </Dialogo>
    </>
  );
}
